import { Component, Input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MsgVO, AttachmentVO } from '../unified-inbox.models';
import { UnifiedInboxService } from '../unified-inbox.service';

@Component({
    selector: 'app-unified-bubble',
    templateUrl: './unified-bubble.component.html',
    styleUrls: ['./unified-bubble.component.scss'],
    standalone: false
})
export class UnifiedBubbleComponent {
    @Input() msg: MsgVO;

    expanded = false;
    attachments: AttachmentVO[] = [];
    attachmentsLoading = false;
    attachmentsLoaded = false;

    constructor(private sanitizer: DomSanitizer, private inboxService: UnifiedInboxService) {}

    /** True when the body contains HTML markup. */
    get hasHtml(): boolean {
        const b = this.msg?.bodyHtml || this.msg?.body || '';
        return b.includes('<') && (b.includes('</') || b.includes('/>'));
    }

    /** Safe HTML for [innerHTML] — Angular strips scripts/event handlers. */
    get safeHtml(): SafeHtml {
        const html = this.msg?.bodyHtml || this.msg?.body || '';
        return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    /** Plain text stripped of HTML tags — used for collapsed preview.
     *  Falls back to bodyHtml when body is empty (KBM outbound emails
     *  store content only in emailHtml/bodyHtml, not emailText/body). */
    get plainText(): string {
        const src = this.msg?.body || this.msg?.bodyHtml || '';
        if (!src.includes('<')) { return src; }
        try {
            const doc = new DOMParser().parseFromString(src, 'text/html');
            return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
        } catch {
            return src.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        }
    }

    /** First 300 chars of plain text shown when bubble is collapsed. */
    get previewText(): string {
        const t = this.plainText;
        return t.length > 300 ? t.substring(0, 300) + '\u2026' : t;
    }

    get isLongBody(): boolean {
        return this.plainText.length > 300;
    }

    toggleExpand(): void {
        this.expanded = !this.expanded;
        if (this.expanded && this.msg?.attachmentCount > 0 && !this.attachmentsLoaded) {
            this.loadAttachments();
        }
    }

    loadAttachments(): void {
        if (!this.msg?.msgId || this.attachmentsLoaded) { return; }
        this.attachmentsLoading = true;
        this.inboxService.getAttachments(this.msg.msgId).subscribe({
            next: res => {
                // ApiResponse wraps data as { data: { data: { attachments: [...] } } }
                // because the controller calls new ApiResponse(200, "OK", "data", dataMap).
                this.attachments = res?.data?.data?.attachments || [];
                this.attachmentsLoaded = true;
                this.attachmentsLoading = false;
            },
            error: () => {
                this.attachmentsLoading = false;
                this.attachmentsLoaded = true;
            }
        });
    }

    channelIcon(channel: string): string {
        if (!channel) { return ''; }
        const c = channel.toLowerCase();
        if (c === 'sms') { return '💬'; }
        if (c === 'email') { return '✉'; }
        if (c === 'note') { return '📝'; }
        if (c.includes('vrbo')) { return '⚙'; }
        if (c.includes('call')) { return '📞'; }
        return '⚙';
    }
}
